"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.cvv2OrCvc = exports.expirationLabel = exports.getVirtualCardTypeLabel = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const ContentCopy_1 = __importDefault(require("@mui/icons-material/ContentCopy"));
const material_1 = require("@mui/material");
const react_query_1 = require("@tanstack/react-query");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const react_use_1 = require("react-use");
const appConstants_1 = require("../../../../constants/appConstants");
const companyQuery_1 = require("../../../../queries/companyQuery");
const userQuery_1 = require("../../../../queries/userQuery");
const setTime_1 = require("../../../../utils/setTime");
const translationUtil_1 = require("../../../../utils/translationUtil");
const requestDetail_1 = require("../../request/newRequest/requestDetail");
const virtualCard_1 = require("../../request/virtualCard");
const bouncingLoaderComp_1 = __importDefault(require("../component/bouncingLoaderComp"));
const truncatedRequesterFullNameText_1 = __importDefault(require("../component/truncatedRequesterFullNameText"));
const lexico = new lexico_1.default();
function isExpired(card) {
    return card?.daysRemaining < 0;
}
function isCanceled(card) {
    return card?.status === 'CANCELED' ||
        card?.status === 'UNAVAILABLE' ||
        card?.status === 'CREATION_ERROR' ||
        card?.status === 'UNKNOWN'
        ? true
        : !isExpired(card) && card?.status === 'EXPIRED';
}
function isActive(card) {
    return !isExpired(card) && !isCanceled(card);
}
const getVirtualCardTypeLabel = ({ card, fields, }) => {
    if (!card) {
        return '';
    }
    const cardTypeLabel = appConstants_1.virtualCardTypes.find(({ cardsKey }) => cardsKey === card?.cardType)?.label || '';
    if (card?.cardType === 'RECURRING_USE') {
        const frequency = card.frequency || fields?.frequency;
        if (!frequency) {
            return '';
        }
        const frequencyLabel = frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase() : '';
        return frequencyLabel + ' ' + cardTypeLabel;
    }
    else {
        return cardTypeLabel || '';
    }
};
exports.getVirtualCardTypeLabel = getVirtualCardTypeLabel;
function getStatusLabel(card) {
    if (isExpired(card)) {
        return 'Expired';
    }
    else if (isCanceled(card)) {
        return (0, translationUtil_1.getCancelledWord)('Canceled');
    }
    else {
        return 'Active';
    }
}
function getVirtualCardStatusAndLabel({ card, fields }) {
    const statusLabel = isExpired(card) || isCanceled(card) ? `${getStatusLabel(card)} - ` : '';
    return statusLabel + (0, exports.getVirtualCardTypeLabel)({ card, fields });
}
function mapCardStatusToMessage(status) {
    switch (status) {
        case 'IN_PROGRESS': {
            return {
                message: lexico.tr('Card is not active yet. All card details will be available once the card is active.'),
                type: 'warning',
            };
        }
        case null:
        case undefined:
        case 'UNKNOWN':
        case 'UNAVAILABLE': {
            return {
                message: lexico.tr('An error occurred while retrieving card details. If the problem persists, please contact support.'),
                type: 'error',
            };
        }
        case 'CREATION_ERROR': {
            return {
                message: lexico.tr("Card creation failed on the provider's side."),
                type: 'error',
            };
        }
        default: {
            return null;
        }
    }
}
exports.expirationLabel = globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? 'Expires End' : 'EXP DATE';
exports.cvv2OrCvc = globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? 'cvv2' : 'cvc';
const WalletCard = ({ task, children }) => {
    const initialData = (0, react_router_1.useLoaderData)();
    const { data: currentUser } = (0, react_query_1.useQuery)(userQuery_1.userQuery);
    const { data: companyPropertiesData } = (0, react_query_1.useQuery)((0, companyQuery_1.companyPropertiesQuery)(currentUser?.companyAccountId || '', { cardprovider: 'tsgo-cardprovider' }));
    const isAmexCardProvider = companyPropertiesData?.cardprovider === 'AMEX';
    const [isHovering, setIsHovering] = (0, react_1.useState)(false);
    const [copyDetails, setCopyDetails] = (0, react_1.useState)({ isCopied: false, copySuccessMessage: '' });
    const fields = task?.context?.request?.fields;
    const cardNumber = (0, virtualCard_1.getCardNumber)(task?.card);
    const requesterFullName = (0, react_1.useMemo)(() => (0, requestDetail_1.getFullName)(task?.request?.requester || {}), [task?.request?.requester]);
    const isCopyAllowed = !Number.isNaN(Number(cardNumber));
    const isCardOwner = task?.assignees?.includes(initialData?.userData?.userId);
    const theme = (0, material_1.useTheme)();
    const [isReady] = (0, react_use_1.useTimeout)((0, setTime_1.setTime)(5, 'seconds'));
    const errorNotification = isReady() || task?.card?.status ? mapCardStatusToMessage(task?.card?.status) : '';
    const copyItems = {
        number: {
            name: 'card number',
            data: cardNumber,
        },
        cvc: {
            name: exports.cvv2OrCvc,
            data: (0, virtualCard_1.getCVVNumber)(task?.card, companyPropertiesData?.cardprovider || ''),
        },
    };
    const virtualCardLabelAndStatus = getVirtualCardStatusAndLabel({ card: task?.card, fields });
    const formattedExpirationDate = (0, virtualCard_1.getFormattedExpiration)(task?.card?.expirationDate);
    const setCopyItem = (copyItemKey, event) => {
        event.stopPropagation();
        if (!isCardOwner || !copyItems[copyItemKey] || !isCopyAllowed) {
            return;
        }
        const copyItem = copyItems[copyItemKey];
        setIsHovering(true);
        setCopyDetails({
            isCopied: false,
            copySuccessMessage: 'Copied ' + copyItem?.name + '!',
        });
    };
    async function onCopySuccess(copyItemKey, event) {
        event.stopPropagation();
        if (!isCardOwner || !copyItems[copyItemKey] || !isCopyAllowed) {
            return;
        }
        const copyItem = copyItems[copyItemKey];
        await navigator.clipboard.writeText(copyItem.data);
        setCopyDetails({
            isCopied: true,
            copySuccessMessage: 'Copied ' + copyItem?.name + '!',
        });
    }
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [errorNotification && isCardOwner && ((0, jsx_runtime_1.jsx)(material_1.Alert, { sx: { width: '100%', marginBottom: theme.tsGoUnit.tsUnitHalf }, "data-testid": "card-status-alert", severity: errorNotification.type, children: errorNotification.message })), (0, jsx_runtime_1.jsxs)(virtualCard_1.StyledCard, { onMouseOver: event => setCopyItem('number', event), onMouseOut: () => setIsHovering(false), onClick: event => onCopySuccess('number', event), sx: [
                    {
                        cursor: isCopyAllowed && isHovering ? 'pointer' : 'default',
                        width: '290px',
                    },
                    globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                        ? {
                            width: '340px',
                            height: '255px',
                            paddingTop: '40px',
                        }
                        : {},
                ], "data-testid": "wallet-card", children: [isAmexCardProvider && virtualCardLabelAndStatus ? (isReady() || task?.card?.status ? ((0, jsx_runtime_1.jsx)(virtualCard_1.StyledChip, { label: virtualCardLabelAndStatus, color: isActive(task?.card) ? 'success' : 'error' })) : ((0, jsx_runtime_1.jsx)(bouncingLoaderComp_1.default, {}))) : null, (0, jsx_runtime_1.jsx)(virtualCard_1.StyledHoverText, { children: isCopyAllowed && isHovering ? (copyDetails.isCopied ? copyDetails.copySuccessMessage : '') : '' }), (0, jsx_runtime_1.jsxs)(virtualCard_1.StyledGrid, { className: isCopyAllowed ? 'isRevealDetailsAllowed' : '', children: [(0, virtualCard_1.formatCardNumber)(task?.card).map((cardNumberPiece, index) => ((0, jsx_runtime_1.jsx)(material_1.Typography, { sx: { fontSize: '22px', color: 'white' }, children: cardNumberPiece }, cardNumberPiece + index))), isCopyAllowed && ((0, jsx_runtime_1.jsx)(ContentCopy_1.default, { sx: {
                                    fontSize: theme.typography.pxToRem(14),
                                    color: 'hsl(0, 0%, 100%)',
                                    marginLeft: theme.tsGoUnit.tsUnitQuarter,
                                } }))] }), (0, jsx_runtime_1.jsxs)(virtualCard_1.StyledBox, { style: {
                            ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                ? {
                                    paddingTop: '20px',
                                }
                                : {}),
                        }, children: [formattedExpirationDate ? ((0, jsx_runtime_1.jsxs)(virtualCard_1.StyledElement, { children: [exports.expirationLabel ? ((0, jsx_runtime_1.jsx)(virtualCard_1.ExpirationLabel, { style: {
                                            ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                                ? {
                                                    fontSize: '16px',
                                                }
                                                : {}),
                                        }, children: exports.expirationLabel })) : null, (0, jsx_runtime_1.jsx)(virtualCard_1.ExpirationValue, { style: {
                                            ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                                ? {
                                                    fontSize: '22px',
                                                }
                                                : {}),
                                        }, "data-testid": "expirationDate", children: formattedExpirationDate })] })) : null, (0, jsx_runtime_1.jsxs)(virtualCard_1.StyledElement, { onMouseOver: event => setCopyItem('cvc', event), onMouseOut: () => setIsHovering(false), onClick: event => onCopySuccess('cvc', event), sx: { display: 'flex', justifyContent: 'flex-end' }, children: [(0, jsx_runtime_1.jsx)(virtualCard_1.ExpirationLabel, { style: {
                                            ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                                ? {
                                                    fontSize: '16px',
                                                }
                                                : {}),
                                        }, children: exports.cvv2OrCvc }), (0, jsx_runtime_1.jsx)(virtualCard_1.ExpirationValue, { style: {
                                            ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                                ? {
                                                    fontSize: '22px',
                                                }
                                                : {}),
                                        }, children: (0, virtualCard_1.getCVVNumber)(task?.card, companyPropertiesData?.cardprovider || '') }), isCopyAllowed && ((0, jsx_runtime_1.jsx)(ContentCopy_1.default, { sx: { fontSize: theme.typography.pxToRem(14), color: 'hsl(0, 0%, 100%)' } }))] })] }), (0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                ? {
                                    bottom: '-20px',
                                    position: 'relative',
                                }
                                : {}),
                        }, children: [requesterFullName ? ((0, jsx_runtime_1.jsx)(truncatedRequesterFullNameText_1.default, { text: requesterFullName, fontSize: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? '16px' : '12px' })) : null, globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? ((0, jsx_runtime_1.jsx)(material_1.Grid, { size: 2, children: (0, jsx_runtime_1.jsx)(virtualCard_1.ProviderImage, { provider: task?.card?.provider || 'N/A' }) })) : null] }), children] })] }));
};
exports.default = WalletCard;
